import { Link } from "react-router-dom";

import { usePatientName } from "features/communications/hooks/usePatientName";
import { pathGenerators } from "utils/routes";

import styles from "./NeedsAttentionTable.module.css";

/**
 * The name behind a row's patientId. `/unlinked` carries no patient name — the list
 * columns join the consultant, not the patient — so it is resolved here, per row.
 * The queue is an exception state and stays short, and each name is cached for ten
 * minutes by usePatientName, so this is a handful of requests rather than a fan-out.
 *
 * A matched row links to the patient's pregnancy tab (KIN-1969). A NO_PREGNANCY row
 * tells the GP that sending stays blocked until a pregnancy is attached, and then used
 * to give them nowhere to go — they had to carry the name in their head, search for the
 * patient and find the tab. It opens in a new tab so the queue keeps its place, since
 * this is a list a GP works through rather than a single errand.
 */
export const UnlinkedPatientName = ({ patientId }) => {
  const { data: patient, isLoading } = usePatientName(patientId);

  if (!patientId) return <span className={styles.muted}>Not matched</span>;
  if (isLoading) return <span className={styles.muted}>Loading…</span>;

  const name = `${patient?.firstName ?? ""} ${patient?.surname ?? patient?.lastName ?? ""}`.trim();

  // The id is what makes the link work, so a name that never resolved is still clickable.
  return (
    <Link
      className={styles.patientLink}
      to={pathGenerators.patient.pregnancy(patientId)}
      target="_blank"
      rel="noreferrer"
      title="Open this patient's pregnancy tab in a new tab"
    >
      {name || `Patient #${patientId}`}
    </Link>
  );
};
